/**
 * Operator review screen for a renewal PROPOSAL.
 *
 * Pulse prepares and sends renewal proposals; it never issues invoices —
 * FinOps does that later. Two things the UI must make unmistakable:
 *   * Approving does NOT send.
 *   * Sending does NOT make it the commercial baseline. Only the
 *     customer accepting does.
 *
 * Accessibility: change indicators are text + symbol, never colour
 * alone; tables carry captions and scoped headers; the send dialog is a
 * definition list of the exact terms being transmitted.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle, Clock, FileText, Lock } from "@phosphor-icons/react";
import {
  money, StatusChip, SourceCell, ProvenanceNote,
} from "./BillingSpecificationPanels";
import { isHistoricalSpec, isSettledSpec, invoiceDateOf } from "@/lib/billingProvenance";

const CHANGE_META = {
  added: { label: "Added", mark: "+" },
  removed: { label: "Removed", mark: "−" },
  quantity_changed: { label: "Quantity changed", mark: "≠" },
  users_changed: { label: "Users changed", mark: "≠" },
  price_changed: { label: "Price changed", mark: "≠" },
  discount_changed: { label: "Credit changed", mark: "≠" },
  unchanged: { label: "Unchanged", mark: "=" },
};

function ChangeChip({ type }) {
  const m = CHANGE_META[type] || CHANGE_META.unchanged;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
      <span aria-hidden="true">{m.mark}</span>
      {m.label}
    </span>
  );
}

function Tile({ label, value, hint, emphasis }) {
  return (
    <div className={`border p-4 ${emphasis ? "border-2 border-[#0055FF] bg-[#EFF6FF]" : "border-[#E5E5E5] bg-white"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider ${emphasis ? "text-[#0055FF]" : "text-[#A3A3A3]"}`}>
        {label}
      </div>
      <div className="text-xl font-black mt-1 tracking-tight text-[#0A0A0A]">{value}</div>
      {hint && <div className="text-[11px] text-[#A3A3A3] mt-0.5">{hint}</div>}
    </div>
  );
}

export default function RenewalReviewPanel({ specId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [upliftInput, setUpliftInput] = useState("0");
  const [sendOpen, setSendOpen] = useState(false);
  const [recipient, setRecipient] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/renewals/${specId}`);
      setData(r.data);
      setUpliftInput(String(r.data.renewal?.uplift_pct ?? 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the renewal proposal");
    }
    setLoading(false);
  }, [specId]);

  useEffect(() => { load(); }, [load]);

  const act = async (path, msg, opts = {}) => {
    setBusy(true);
    try {
      await api.post(`/renewals/${specId}/${path}`, opts.body ?? null, { params: opts.params });
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  const applyUplift = async () => {
    const pct = Number(upliftInput);
    if (!Number.isFinite(pct)) {
      toast.error("Enter a valid percentage.");
      return;
    }
    await act("uplift", `Annual increase set to ${pct}%`, { body: { uplift_pct: pct } });
  };

  const doSend = async () => {
    const s = data.summary;
    setBusy(true);
    try {
      const r = await api.post(`/renewals/${specId}/send-to-client`, {
        recipient_email: recipient,
        confirm_recurring_annual_value: s.proposed_recurring_annual_value,
        confirm_uplift_pct: s.uplift_pct ?? 0,
      });
      toast.success(`Renewal proposal ${r.data.quote_number} sent to ${r.data.recipient}`);
      setSendOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setBusy(false);
  };

  if (loading) return <div className="h-32 bg-[#F5F5F5] animate-pulse" />;
  if (!data) return null;

  const { renewal: r, client, summary: s, comparison, editable, can_send } = data;
  const cur = client.currency_code;
  const sent = !!r.sent_to_client_at;
  const accepted = !!r.client_accepted_at;

  // A settled specification — active or historical — is agreed commercial
  // truth, not something being proposed. Presenting it with proposal
  // wording ("proposed", "removed") invites an operator to read a closed
  // record as a pending change, so the framing follows the record's own
  // state. Nothing here keys off which client it belongs to.
  const historical = isHistoricalSpec(r.status);
  const settled = isSettledSpec(r.status);
  const heading = historical
    ? "Historical specification"
    : settled ? "Current specification" : "Renewal proposal";
  // Lines absent from this specification but present in today's ledger
  // are not "removed" by it — they simply postdate it.
  const rows = settled
    ? comparison.filter((c) => c.change_type !== "removed")
    : comparison;

  return (
    <div className="space-y-4" data-testid="renewal-review-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-black text-[#0A0A0A]">
            {heading} &middot; {client.name}
          </h3>
          <p className="text-xs text-[#525252] mt-0.5">
            Period {String(r.period_start || "").slice(0, 10)} &rarr;{" "}
            {String(r.period_end || "").slice(0, 10)} &middot; {cur}
          </p>
          <dl className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Source</dt>
              <dd data-testid="spec-source"><SourceCell spec={r} /></dd>
            </div>
            <div className="flex items-center gap-1.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Invoice date</dt>
              <dd className="font-mono text-[#525252]" data-testid="spec-invoice-date">
                {invoiceDateOf(r) || <span className="text-[#D4D4D4]">Not documented</span>}
              </dd>
            </div>
          </dl>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={r.status} />
          {onClose && (
            <Button onClick={onClose} size="sm"
                    className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
              Back
            </Button>
          )}
        </div>
      </div>

      {historical ? (
        <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-2.5 text-xs text-[#525252] flex items-center gap-2"
             data-testid="historical-record-banner">
          <Lock size={14} aria-hidden="true" />
          Historical record &mdash; the agreed terms for a period that has closed. It is
          permanent commercial history, not a pending change.
        </div>
      ) : accepted ? (
        <div className="border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-2.5 text-xs text-[#166534] flex items-center gap-2">
          <CheckCircle size={14} weight="fill" aria-hidden="true" />
          Accepted by the client on {String(r.client_accepted_at).slice(0, 10)}. This is now
          their current recurring specification and is a permanent record.
        </div>
      ) : sent ? (
        <div className="border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-2.5 text-xs text-[#1E40AF] flex items-center gap-2">
          <Clock size={14} aria-hidden="true" />
          Sent to the client on {String(r.sent_to_client_at).slice(0, 10)} &mdash; awaiting
          their acceptance. It does not take effect until they accept.
        </div>
      ) : (
        <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-2.5 text-xs text-[#525252] flex items-center gap-2">
          <FileText size={14} aria-hidden="true" />
          Internal only &mdash; nothing has been sent to the client.
        </div>
      )}

      {settled ? (
        /* A closed record states what it was, not what it proposes. Any
           "increase" here would be measured against today's ledger, which
           is not the period this record actually followed. */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Tile label="Annual recurring value"
                value={money(s.proposed_recurring_annual_value, cur)}
                hint="The agreed terms for this period" emphasis />
          <Tile label="One-time total" value={money(r.one_time_total, cur)}
                hint="Historical — never renews" />
          <Tile label="Billing basis" value={money(r.initial_billing_total, cur)}
                hint="One-time + recurring for this period" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Tile label="Current annual value" value={money(s.prior_recurring_annual_value, cur)} />
          <Tile label="Annual increase"
                value={`${Number(s.uplift_pct ?? 0).toFixed(2)}%`}
                hint={money(s.uplift_amount, cur)} />
          <Tile label="Proposed annual value"
                value={money(s.proposed_recurring_annual_value, cur)} emphasis />
          <Tile label="Year over year"
                value={money(s.year_over_year_change, cur)}
                hint={`${Number(s.year_over_year_pct ?? 0).toFixed(2)}%`} />
        </div>
      )}

      <ProvenanceNote notes={r.notes} title="Commercial interpretation" />

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#525252]">
        <span>
          Licensed users{" "}
          {settled ? (
            <strong>{s.proposed_licensed_users ?? "—"}</strong>
          ) : (
            <>
              <strong>{s.prior_licensed_users ?? "—"}</strong> &rarr;{" "}
              <strong>{s.proposed_licensed_users ?? "—"}</strong>
            </>
          )}
        </span>
        <span>Modules <strong>{s.module_count}</strong></span>
        <span>Integrations <strong>{s.integration_count}</strong></span>
      </div>

      {editable && (
        <div className="border border-[#E5E5E5] bg-white p-4 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="uplift-input"
                   className="block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
              Annual price increase %
            </label>
            <input id="uplift-input" type="number" step="0.01" value={upliftInput}
                   onChange={(e) => setUpliftInput(e.target.value)}
                   className="border border-[#E5E5E5] px-3 py-2 text-sm w-32 font-mono focus:outline-none focus:border-[#0055FF]"
                   data-testid="uplift-input" />
          </div>
          <Button onClick={applyUplift} disabled={busy}
                  className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-9 text-[11px] uppercase"
                  data-testid="apply-uplift">
            Apply to all lines
          </Button>
          <p className="text-[11px] text-[#737373] max-w-md leading-snug">
            Applies to every line except those priced by hand, which keep their override.
            Changing this recalculates from last year&rsquo;s prices, so it never compounds.
          </p>
        </div>
      )}

      <div className="overflow-x-auto border border-[#E5E5E5]">
        <table className="w-full text-xs">
          <caption className="sr-only">
            {settled
              ? "Lines recorded on this specification"
              : "Prior year compared with the proposed renewal"}
          </caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              <th scope="col" className="text-left px-3 py-2.5">Line</th>
              <th scope="col" className="text-left px-3 py-2.5">Category</th>
              <th scope="col" className="text-right px-3 py-2.5">Users</th>
              {!settled && <th scope="col" className="text-right px-3 py-2.5">Current</th>}
              <th scope="col" className="text-right px-3 py-2.5">
                {settled ? "Annual" : "Proposed"}
              </th>
              {!settled && <th scope="col" className="text-left px-3 py-2.5">Change</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.line_id || `removed-${i}`} className="border-t border-[#F5F5F5]"
                  data-testid={`renewal-compare-${c.line_id || i}`}>
                <td className="px-3 py-2.5 font-semibold text-[#0A0A0A]">
                  {c.description}
                  {c.is_price_overridden && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#92400E]">
                      manual price
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[#525252]">{c.category_label}</td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {settled
                    ? (c.proposed_users ?? <span className="text-[#D4D4D4]">&mdash;</span>)
                    : c.prior_users != null || c.proposed_users != null
                      ? `${c.prior_users ?? "—"} → ${c.proposed_users ?? "—"}`
                      : <span className="text-[#D4D4D4]">&mdash;</span>}
                </td>
                {!settled && (
                  <td className="px-3 py-2.5 text-right font-mono text-[#525252]">
                    {c.prior_annual != null
                      ? money(c.prior_annual, cur)
                      : <span className="text-[#D4D4D4]">&mdash;</span>}
                  </td>
                )}
                <td className="px-3 py-2.5 text-right font-mono font-semibold">
                  {!settled && c.change_type === "removed"
                    ? <span className="text-[#737373]">not renewed</span>
                    : money(c.proposed_annual, cur)}
                </td>
                {!settled && (
                  <td className="px-3 py-2.5"><ChangeChip type={c.change_type} /></td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#0A0A0A] bg-[#FAFAFA] font-bold">
              <td className="px-3 py-2.5" colSpan={3}>Annual recurring value</td>
              {!settled && (
                <td className="px-3 py-2.5 text-right font-mono">
                  {money(s.prior_recurring_annual_value, cur)}
                </td>
              )}
              <td className="px-3 py-2.5 text-right font-mono text-[#0055FF]">
                {money(s.proposed_recurring_annual_value, cur)}
              </td>
              {!settled && <td className="px-3 py-2.5" />}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* A settled record has no lifecycle actions left — rendering the
          empty action bar under one only implies otherwise. */}
      {!accepted && !settled && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[#E5E5E5] pt-3">
          {r.status === "draft" && !sent && (
            <Button disabled={busy} onClick={() => act("submit-for-review", "Sent for review")}
                    className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
              Submit for review
            </Button>
          )}
          {["draft", "ready_for_review"].includes(r.status) && !sent && (
            <Button disabled={busy} onClick={() => act("approve", "Approved — not yet sent")}
                    className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase"
                    data-testid="renewal-approve">
              Approve
            </Button>
          )}
          {can_send && (
            <>
              <Button disabled={busy}
                      onClick={() => { setRecipient(""); setSendOpen(true); }}
                      className="bg-[#166534] hover:bg-[#14532D] text-white rounded-none h-8 text-[11px] uppercase"
                      data-testid="renewal-send">
                Send renewal proposal to client
              </Button>
              <Button disabled={busy} onClick={() => act("reopen", "Reopened — approval cleared")}
                      className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
                Reopen to edit
              </Button>
            </>
          )}
          {!sent && ["draft", "ready_for_review", "approved"].includes(r.status) && (
            <Button disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Cancel this renewal proposal?")) return;
                      act("cancel", "Renewal proposal cancelled");
                    }}
                    className="bg-white border border-[#E5E5E5] text-[#991B1B] rounded-none h-8 text-[11px] uppercase">
              Cancel
            </Button>
          )}
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle>Send this renewal proposal to the client?</DialogTitle>
            <DialogDescription className="text-xs">
              This emails the customer. Check the commercial terms below before confirming.
            </DialogDescription>
          </DialogHeader>
          <dl className="text-xs border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
            {[
              ["Client", client.name],
              ["Renewal period",
               `${String(r.period_start || "").slice(0, 10)} → ${String(r.period_end || "").slice(0, 10)}`],
              ["Currency", cur],
              ["Current annual value", money(s.prior_recurring_annual_value, cur)],
              ["Annual increase",
               `${Number(s.uplift_pct ?? 0).toFixed(2)}% (${money(s.uplift_amount, cur)})`],
              ["Proposed annual value", money(s.proposed_recurring_annual_value, cur)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between px-3 py-2">
                <dt className="text-[#525252]">{k}</dt>
                <dd className="font-mono font-semibold text-[#0A0A0A]">{v}</dd>
              </div>
            ))}
          </dl>
          <div>
            <label htmlFor="renewal-recipient"
                   className="block text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">
              Send to
            </label>
            <input id="renewal-recipient" type="email" value={recipient}
                   onChange={(e) => setRecipient(e.target.value)}
                   placeholder="billing@client.example.com"
                   className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                   data-testid="renewal-recipient" />
          </div>
          <p className="text-[11px] text-[#737373] leading-relaxed">
            The client receives a proposal with Accept / Reject links. It becomes their
            commercial terms only when they accept. No invoice is created &mdash; invoicing
            is handled separately by FinOps.
          </p>
          <DialogFooter>
            <Button onClick={() => setSendOpen(false)}
                    className="bg-white border border-[#E5E5E5] rounded-none">
              Cancel
            </Button>
            <Button onClick={doSend} disabled={busy || !recipient.includes("@")}
                    className="bg-[#166534] hover:bg-[#14532D] text-white rounded-none"
                    data-testid="renewal-send-confirm">
              {busy ? "Sending…" : "Confirm and send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
